'use client'
import { type FC, useMemo } from 'react'
import type { CompanyAddressDTO, CountryCode } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUser } from 'auth'
import { useRememberedAddress } from '../../../hooks'
import { CompanyAddressPicker } from './CompanyAddressPicker'
import { type OrderAddressWithContact } from './CustomAddressForm'

// Override keys keep the legacy `invoice*` prefix so the existing
// `invoicePaymentTranslations` bundle resolves them across all flows.
export interface BillingAddressPickerOverrides {
  invoiceBillingHeading?: string
  invoiceBillingFoldedHint?: string
  invoiceBillingChange?: string
  invoiceBillingCancel?: string
  invoiceBillingSaveCustom?: string
  invoiceBillingCustomOption?: string
  invoiceBillingEmpty?: string
  invoiceBillingFieldAttention?: string
  invoiceBillingFieldAddress1?: string
  invoiceBillingFieldAddress2?: string
  invoiceBillingFieldPostalCode?: string
  invoiceBillingFieldPostalRegion?: string
  invoiceBillingFieldCountry?: string
  invoiceBillingFieldMobilePhone?: string
  invoiceBillingFieldEmail?: string
  invoiceBillingFieldFirstName?: string
  invoiceBillingFieldLastName?: string
  invoiceBillingErrorRequired?: string
  invoiceBillingErrorEmail?: string
  invoiceBillingErrorPhone?: string
  invoiceBillingFieldReference?: string
  invoiceBillingRememberOnDevice?: string
}

interface BillingAddressPickerProps {
  value: OrderAddressWithContact | undefined
  onChange: (next: OrderAddressWithContact) => void
  /**
   * Require a contact person, phone and email on the custom-address form.
   *
   * Used by the Briqpay flows, where the payment widget is payment-only and nothing else
   * collects the shopper's identity. Left off elsewhere so the invoice flow is unchanged.
   */
  requireContactDetails?: boolean
  /** Render the free-text reference field. Default on; off where nothing reads it. */
  showReference?: boolean
  /**
   * Preselect the country for a shopper with no saved address — normally the market they
   * picked in the checkout summary. The dropdown stays unfiltered: a billing address is
   * not tied to the session's country.
   */
  defaultCountry?: CountryCode
}

/**
 * Billing-address picker shared by the invoice and upfront-DEFAULT flows.
 * Thin wrapper around `CompanyAddressPicker`: filters the logged-in
 * customer's company addresses to `type === 'BILLING'` and supplies
 * billing-specific labels from `OverridesContextProvider`.
 */
export const BillingAddressPicker: FC<BillingAddressPickerProps> = ({
  value,
  onChange,
  requireContactDetails,
  showReference,
  defaultCountry
}) => {
  const overrides = useOverridesContext<keyof BillingAddressPickerOverrides>()
  const { user } = useUser()
  // Records which option the shopper settles on so it comes back preselected. Reading
  // it happens in `useBillingAddress`, which owns the precedence; here we only write.
  const { remember, isOptedIn, setOptedIn } = useRememberedAddress('billing')

  const billingAddresses = useMemo<CompanyAddressDTO[]>(
    () => (user?.customer?.companyProfile?.addresses ?? []).filter((a) => a.type === 'BILLING'),
    [user?.customer?.companyProfile?.addresses]
  )

  const labels = {
    heading: overrideText('Faktureringsadress', overrides?.invoiceBillingHeading),
    foldedHint: overrideText('Vald adress', overrides?.invoiceBillingFoldedHint),
    change: overrideText('Ändra', overrides?.invoiceBillingChange),
    cancel: overrideText('Avbryt', overrides?.invoiceBillingCancel),
    save: overrideText('Spara adress', overrides?.invoiceBillingSaveCustom),
    customOption: overrideText('Anpassad adress', overrides?.invoiceBillingCustomOption),
    empty: overrideText('Ingen faktureringsadress vald.', overrides?.invoiceBillingEmpty),
    attention: overrideText('Att / Kontaktperson', overrides?.invoiceBillingFieldAttention),
    address1: overrideText('Adress', overrides?.invoiceBillingFieldAddress1),
    address2: overrideText('Adress rad 2', overrides?.invoiceBillingFieldAddress2),
    postalCode: overrideText('Postnummer', overrides?.invoiceBillingFieldPostalCode),
    postalRegion: overrideText('Ort', overrides?.invoiceBillingFieldPostalRegion),
    country: overrideText('Land', overrides?.invoiceBillingFieldCountry),
    mobilePhone: overrideText('Telefon', overrides?.invoiceBillingFieldMobilePhone),
    email: overrideText('E-post', overrides?.invoiceBillingFieldEmail),
    phoneError: overrideText('Ogiltigt telefonnummer', overrides?.invoiceBillingErrorPhone),
    firstName: overrideText('Förnamn', overrides?.invoiceBillingFieldFirstName),
    lastName: overrideText('Efternamn', overrides?.invoiceBillingFieldLastName),
    requiredError: overrideText('Obligatoriskt fält', overrides?.invoiceBillingErrorRequired),
    emailError: overrideText('Ogiltig e-postadress', overrides?.invoiceBillingErrorEmail),
    reference: overrideText('Referens', overrides?.invoiceBillingFieldReference),
    rememberOnDevice: overrideText(
      'Kom ihåg mitt val på den här enheten',
      overrides?.invoiceBillingRememberOnDevice
    )
  }

  return (
    <CompanyAddressPicker
      addresses={billingAddresses}
      labels={labels}
      onChange={onChange}
      defaultCountry={defaultCountry}
      onChoose={remember}
      rememberOnDevice={{ checked: isOptedIn, onChange: setOptedIn }}
      requireContactDetails={requireContactDetails}
      showReference={showReference}
      value={value}
    />
  )
}
